import { TimelineItemStatus, TimelineStatus } from '@components'

import { Flex, SkeletonBox, Text } from '@billy/design-system'
import { PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react'

import { RecurringInvoiceEvent } from '@features/recurringInvoices/types/recurringInvoiceEvent'

import type { GeneratedDocument } from '../../types/generatedDocument'

const SKELETON_BOX_WIDTH = 218
const SKELETON_BOX_HEIGHT = 120
const MIN_NUMBER_OF_VISIBLE_ITEMS = 3

type TimelineItem = RecurringInvoiceEvent | GeneratedDocument

interface RecurringInvoiceTimelineProps<T extends TimelineItem> extends PropsWithChildren {
  dateOnly?: boolean
  getItemDate: (item: T) => string
  getItemStatus?: (item: T) => TimelineItemStatus
  getItemTitle: (item: T) => ReactNode
  isLoading: boolean
  items: T[]
  title: string
  titleSuffix?: ReactNode
  withSeparator?: boolean
  withStatus?: boolean
}

export const RecurringInvoiceTimeline = <T extends TimelineItem>({
  children,
  dateOnly = false,
  getItemDate,
  getItemStatus,
  getItemTitle,
  isLoading,
  items,
  title: titleText,
  titleSuffix,
  withSeparator = true,
  withStatus = true,
}: RecurringInvoiceTimelineProps<T>): ReactElement => {
  const hasMultipleTimelineItems = useMemo(() => items.length > MIN_NUMBER_OF_VISIBLE_ITEMS, [items])

  if (isLoading) {
    return <SkeletonBox width={SKELETON_BOX_WIDTH} height={SKELETON_BOX_HEIGHT} />
  }

  const title = !titleSuffix ? (
    titleText
  ) : (
    <Flex width="100%" justifyContent="space-between">
      <Text variant="bodyLG" weight="medium">
        {titleText}
      </Text>
      {titleSuffix}
    </Flex>
  )

  return (
    <TimelineStatus
      isCollapsed={true}
      isCollapsible={hasMultipleTimelineItems}
      minVisibleItemsCount={MIN_NUMBER_OF_VISIBLE_ITEMS}
      title={title}
    >
      {items.length ? (
        <TimelineStatus.ItemsList>
          {items.map((item) => {
            const date = getItemDate(item)

            return (
              <TimelineStatus.Item
                date={date}
                dateOnly={dateOnly}
                key={date}
                status={getItemStatus?.(item) ?? 'neutral'}
                title={getItemTitle(item)}
                withSeparator={withSeparator}
                withStatus={withStatus}
              />
            )
          })}
        </TimelineStatus.ItemsList>
      ) : (
        children
      )}
    </TimelineStatus>
  )
}
